import { observer } from 'mobx-react';
import { Input, InputType, InputOptionBox, InputErrors, Button } from '../../../../core/components/Index';
import ApplyViewModel from './ApplyViewModel';
import useAsyncEffect from 'use-async-effect';
import { useParams } from 'react-router-dom';
import { Steps } from './ApplyModels';
import { Appl } from '../../../../core/Appl';

const CAStateTaxWithholding = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.StateTaxWithholding, memberId!);
  }, []);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Tax Withholding</h1>
        <p className="text-black text-15px tracking--1percent leading-6">
          To ensure that the correct amount of tax is withheld from your retirement benefits, please set your federal and state tax withholding
          preferences below.{' '}
        </p>
      </div>
      <h4 className="text-gray-700 font-medium text-22px">CA State Tax Withholding</h4>
      <div className="space-y-8 text-black">
        <div className="flex items-center">
          <InputOptionBox
            inputId="stateTaxWithhold"
            label={<span><span className='font-bold'>Do not withhold</span> CA State income tax.</span>}
            value={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold}
            checked={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold === "N"}
            onChange={(e: any) => {
              //vm.onInputChange(e, 'stateTaxWithhold', vm.model.taxWithHolding.stateTaxWithHolding);
              vm.onStateTaxWithholdStatusChange("N", e.target.checked);
            }
            }
          />
        </div>
        <div className="flex items-center my-8 font-bold w-full">
          <div className="relative w-full">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[3px] border-[#E7EAEB]"></div>
            </div>
            <div className="relative pl-6">
              <span className="bg-white px-2 text-black text-15px">OR</span>
            </div>
          </div>
        </div>
        <div>
          <InputOptionBox
            inputId="stateTaxWithhold1"
            label={<span><span className='font-bold'>Withhold CA State income tax</span>, based on the tax tables for your filing status (select one and indicate number of allowances or 0):</span>}
            value={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold}
            checked={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold === "Y"}
            onChange={(e: any) => {
              vm.onStateTaxWithholdStatusChange("Y", e.target.checked);
            }
            }
          />
          <div
            className={`${vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold === 'Y' ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
            <div className="pl-6 mt-2 space-y-2">
              {vm.model.withholdStateIncomeTaxStatusListItems?.map((status, index) => (
                <div key={`stateWithholdIncomeTaxStatus-${index}`} className="flex items-center">
                  <InputOptionBox
                    inputId={`stateWithholdIncomeTaxStatus-${status.key}`}
                    label={`${status.value} with `}
                    value={status.key}
                    checked={vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus === status.key.toString()}
                    onChange={(e: any) => vm.onInputChange(e, 'stateWithholdIncomeTaxStatus', vm.model.taxWithHolding.stateTaxWithHolding)}
                    disabled={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold !== "Y"}
                  />
                  <div>
                    {status.key === "M" &&
                      <Input
                        id="stateMarriedWithholdIncomeAllowance"
                        value={vm.model.taxWithHolding.stateTaxWithHolding?.stateMarriedWithholdIncomeAllowance}
                        inputType={InputType.Number}
                        onChange={(e) => {
                          vm.onInputChange(e, 'stateMarriedWithholdIncomeAllowance', vm.model.taxWithHolding.stateTaxWithHolding);
                        }}
                        className="!w-[80px] ms-2"
                        // className="!w-[120px] [&>input]:rounded-l-none ms-2"
                        readOnly={vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus !== "M"}
                        onKeyDown={((e) => e.code === 'Period' ? e.preventDefault() : {})}
                      />
                    }
                    {status.key === "S" &&
                      <Input
                        id="stateSingleWithholdIncomeAllowance"
                        value={vm.model.taxWithHolding.stateTaxWithHolding?.stateSingleWithholdIncomeAllowance}
                        inputType={InputType.Number}
                        onChange={(e) => {
                          vm.onInputChange(e, 'stateSingleWithholdIncomeAllowance', vm.model.taxWithHolding.stateTaxWithHolding);
                        }}
                        // className="!w-[120px] [&>input]:rounded-l-none ms-2"
                        className="!w-[80px] ms-2"
                        readOnly={vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus !== "S"}
                        onKeyDown={((e) => e.code === 'Period' ? e.preventDefault() : {})}
                      />
                    }
                    {status.key === "H" &&
                      <Input
                        id="stateHeadOfHouseHoldIncomeAllowance"
                        value={vm.model.taxWithHolding.stateTaxWithHolding?.stateHeadOfHouseHoldIncomeAllowance}
                        inputType={InputType.Number}
                        onChange={(e) => {
                          vm.onInputChange(e, 'stateHeadOfHouseHoldIncomeAllowance', vm.model.taxWithHolding.stateTaxWithHolding);
                        }}
                        // className="!w-[120px] [&>input]:rounded-l-none ms-2"
                        className="!w-[80px] ms-2"
                        readOnly={vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus !== "H"}
                        onKeyDown={((e) => e.code === 'Period' ? e.preventDefault() : {})}
                      />
                    }
                  </div>
                </div>
              ))}
              <InputErrors errors={Appl.Error?.items} viewId={"withholdStateIncomeTaxStatus"} />
              <div>
                <h4 className="text-15px font-bold mt-6 leading-6">Additional Withholding (Optional)</h4>
                <p className="text-15px text-black tracking--1percent leading-6 my-1">
                  Withhold the following amount from each benefit payment <b>in addition to</b> the amount to be withheld based on the state tax tables.</p>
                <div className='h-full'>

                  <Input
                    id="stateAdditionalTaxToWithheldBasedOnTax"
                    value={vm.model.taxWithHolding.stateTaxWithHolding?.stateAdditionalTaxToWithheldBasedOnTax}
                    inputType={InputType.Number}
                    onChange={(e) => {
                      vm.onInputChange(e, 'stateAdditionalTaxToWithheldBasedOnTax', vm.model.taxWithHolding.stateTaxWithHolding);
                    }}
                    prefix="$"
                    prefixPosition="left"
                    readOnly={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold !== "Y"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center my-8 font-bold w-full">
          <div className="relative w-full">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[3px] border-[#E7EAEB]"></div>
            </div>
            <div className="relative pl-6">
              <span className="bg-white px-2 text-black text-15px">OR</span>
            </div>
          </div>
        </div>
        <div className=" mt-2 space-y-2">
          <div className="flex items-center">
            <InputOptionBox
              inputId="stateTaxWithhold2"
              label={<span><span className='font-bold'>Withhold only </span>the following amount from each benefit payment.</span>}
              value={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold}
              checked={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold === "P"}
              onChange={(e: any) => {
                vm.onStateTaxWithholdStatusChange("P", e.target.checked);
              }
              }
            />
          </div>
          <div
            className={`${vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold === 'P' ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
            <div className='pl-6 h-full'>
              <Input
                id="stateAdditionalTaxToWithheldBasedOnBenefitPayment"
                value={vm.model.taxWithHolding.stateTaxWithHolding?.stateAdditionalTaxToWithheldBasedOnBenefitPayment}
                inputType={InputType.Number}
                onChange={(e) => {
                  vm.onInputChange(e, 'stateAdditionalTaxToWithheldBasedOnBenefitPayment', vm.model.taxWithHolding.stateTaxWithHolding);
                }}
                prefix="$"
                prefixPosition="left"
                readOnly={vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold !== "P"}
              />
            </div>
          </div>
        </div>

      </div>
      <div className="flex space-x-4 mt-6">
        <Button
          label="Save & Continue" rounded
          disabled={!vm.model.taxWithHolding.stateTaxWithHolding?.stateTaxWithhold}
          onClick={async () => await vm.onStateTaxWithholdStepChangeAsync()}
          className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600"
        />
      </div>
    </div>
  );
});
export default CAStateTaxWithholding;
